import type {
	ColDef,
	ColGroupDef,
	ICellRendererParams,
} from "ag-grid-enterprise";
import { Box, Checkbox } from "@mui/material";
import {
	GROUP_TINT_1,
	GROUP_TINT_2,
} from "@/controls/ag-grid/ColumnGroupTints";
import {
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	TEXT_CELL_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import type { RecipeStand } from "../models/Recipe";

const groupSeparator = "ag-column-group-separator";
const textCell = [ONE_LINE_CELL_CLASS, TEXT_CELL_CLASS];
const numberCell = [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS];

const flagCell =
	(field: "IsTensionControlEnabled" | "IsLooperControlEnabled") =>
	(params: ICellRendererParams<RecipeStand>) => {
		if (!params.data) return null;
		if (params.data[field] == null) return "---";
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
				}}
			>
				<Checkbox
					checked={!!params.data[field]}
					size="small"
					disabled
					sx={{ p: 0 }}
				/>
			</Box>
		);
	};

const decimals = (places: number) => (params: { value: number | null }) =>
	params.value != null ? params.value.toFixed(places) : "---";

/** One row per stand: the mill setup values, the R-Factor band and the looper. */
export const useStandsTableData = () => {
	const columnDefs: (ColDef<RecipeStand> | ColGroupDef<RecipeStand>)[] = [
		{
			field: "StandName",
			headerName: "Stand",
			flex: 0.8,
			minWidth: 60,
			sortable: true,
			suppressSpanHeaderHeight: true,
			cellClass: textCell,
		},
		{
			field: "IsTensionControlEnabled",
			headerName: "TC",
			flex: 0.5,
			minWidth: 45,
			sortable: false,
			suppressSpanHeaderHeight: true,
			cellRenderer: flagCell("IsTensionControlEnabled"),
		},
		{
			field: "RollGroove",
			headerName: "Roll Groove",
			flex: 0.8,
			minWidth: 70,
			suppressSpanHeaderHeight: true,
			headerClass: groupSeparator,
			cellClass: numberCell,
			valueFormatter: decimals(2),
		},
		{
			headerName: "R-Factor",
			marryChildren: true,
			headerClass: [groupSeparator, GROUP_TINT_1],
			children: [
				{
					field: "RFactorTarget",
					headerName: "Target",
					flex: 0.7,
					minWidth: 62,
					cellClass: numberCell,
					valueFormatter: decimals(3),
				},
				{
					field: "RFactorMin",
					headerName: "Min",
					flex: 0.7,
					minWidth: 62,
					cellClass: numberCell,
					valueFormatter: decimals(3),
				},
				{
					field: "RFactorMax",
					headerName: "Max",
					flex: 0.7,
					minWidth: 62,
					cellClass: numberCell,
					valueFormatter: decimals(3),
				},
			],
		},
		{
			headerName: "Looper",
			marryChildren: true,
			headerClass: [groupSeparator, GROUP_TINT_2],
			children: [
				{
					field: "IsLooperControlEnabled",
					headerName: "Scanner",
					flex: 0.6,
					minWidth: 62,
					sortable: false,
					headerClass: groupSeparator,
					cellRenderer: flagCell("IsLooperControlEnabled"),
				},
				{
					field: "LooperHeight",
					headerName: "Height",
					flex: 0.6,
					minWidth: 62,
					cellClass: numberCell,
					valueFormatter: decimals(1),
				},
			],
		},
	];

	return { columnDefs };
};
